/* Events — STORY-011.
 *
 * The shape of an idea. Everything except the name is optional, and that is the
 * story rather than a detail of it: an Event is captured the moment somebody
 * has it, and the product asks for detail later.
 */
import { EVENT_STATUSES } from '@eventuley/domain';
import * as z from 'zod';
/**
 * The five, from `glossary.md`. Moving between them is STORY-012.
 *
 * Declared in `packages/domain` and re-exported here, so the boundary names
 * the domain rather than the reverse — `architecture.md`. Every consumer
 * imports it from here as before; only the direction changed.
 */
export { EVENT_STATUSES } from '@eventuley/domain';
/** Long enough for a real name, short enough to render in a list row. */
export const EVENT_NAME_MAX = 120;
export const EVENT_NOTES_MAX = 4000;
/* A calendar date, not an instant. `YYYY-MM-DD`, because the 14th is the 14th
 * at the Venue whatever zone the reader is in — and an instant would drift
 * across midnight for anybody far enough away.
 *
 * The shape check is not enough on its own, and that is the interesting part:
 * `2026-02-31` and `2026-13-01` both match the pattern, reach Postgres, and
 * raise "date/time field value out of range" — an unexpected throw, which the
 * error boundary turns into an opaque 500 with a logged stack trace. A client's
 * malformed input must be a 400, so the value is parsed as well as matched. */
const dateString = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'A calendar date, as YYYY-MM-DD.')
    .refine((value) => {
    /* Round-tripped rather than fed to `new Date`, which rolls February 31st
     * forward to March 3rd rather than refusing it. */
    const [year, month, day] = value.split('-').map(Number);
    if (year === undefined || month === undefined || day === undefined)
        return false;
    const parsed = new Date(Date.UTC(year, month - 1, day));
    return (parsed.getUTCFullYear() === year &&
        parsed.getUTCMonth() === month - 1 &&
        parsed.getUTCDate() === day);
}, 'That is not a date on any calendar.');
/* `HH:MM`, seconds optional. Stored as a real time value, never as text — and
 * bounded for the same reason as the date: `99:99` matches the shape. */
const timeString = z
    .string()
    .regex(/^\d{2}:\d{2}(:\d{2})?$/, 'A time, as HH:MM.')
    .refine((value) => {
    const [hours = 99, minutes = 99, seconds = 0] = value.split(':').map(Number);
    return hours < 24 && minutes < 60 && seconds < 60;
}, 'That is not a time on any clock.');
export const moneySchema = z.object({
    /* Integer minor units plus a currency, never a decimal or a float —
     * ADR-0005. Zero is legitimate: a night planned to cost nothing is a plan. */
    /* Bounded, and not arbitrarily. The column is `bigint` read as a JavaScript
     * number, so anything past `MAX_SAFE_INTEGER` is either read back with lost
     * precision or refused by Postgres as a numeric overflow — a 500 for what is
     * a client's bad input. `packages/domain` guards the same edge with
     * `Number.isSafeInteger`; this is the wire's half of it. */
    amount: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
    currency: z
        .string()
        .length(3)
        .regex(/^[A-Z]{3}$/, 'A three-letter ISO 4217 code, uppercase.'),
});
export const eventOrganiserSchema = z.object({
    userId: z.string(),
    displayName: z.string(),
    /* True when the account behind this Organiser has been erased — ADR-0015.
     * The Event still had somebody accountable for it, and the client renders the
     * one fixed anonymous label from the catalogue rather than a stored string. */
    anonymised: z.boolean(),
});
/** The Venue an Event is held at, as much of it as the Event needs. */
export const eventVenueSchema = z.object({
    id: z.string(),
    name: z.string(),
    timezone: z.string().nullable(),
    /* Carried so a picker can keep a hidden Venue selected and MARK it. Omitting
     * it would blank the Venue the moment somebody edited an unrelated field. */
    available: z.boolean(),
});
export const eventSchema = z.object({
    id: z.string(),
    name: z.string(),
    status: z.enum(EVENT_STATUSES),
    date: dateString.nullable(),
    time: timeString.nullable(),
    plannedBudget: moneySchema.nullable(),
    expectedRevenue: moneySchema.nullable(),
    notes: z.string().nullable(),
    venue: eventVenueSchema.nullable(),
    /**
     * The zone this Event's times are read in.
     *
     * **Resolved on every read, never stored** — STORY-013 and EPIC-002 risk 2.
     * The Venue's zone if it has one, otherwise the workspace's. Storing it would
     * freeze the answer at the moment a Venue was attached, so moving an Event
     * abroad would leave it displaying the old country's clock.
     *
     * Attaching a Venue in another zone therefore leaves the wall-clock time
     * exactly as it was and changes only the zone shown beside it.
     */
    timezone: z.string(),
    organisers: z.array(eventOrganiserSchema),
    createdAt: z.string(),
});
/**
 * One Event, fetched by its own id — added 2026-09-05.
 *
 * The detail screen used to pick its Event out of the LIST response, which
 * silently made two ordinary things unreachable: an Event whose status is not
 * listed by default (completed, cancelled) and any Event past the first page.
 * Cancelling one from its own page therefore ended on "that event is not
 * here", which reads as though it had been destroyed — and Events cannot be
 * deleted at all, so that reading was alarming and wrong.
 *
 * A record is addressed by its id, never found by filtering a page of records.
 */
export const eventResponseSchema = z.object({ event: eventSchema });
export const eventsResponseSchema = z.object({
    events: z.array(eventSchema),
    /* How many match the filter in total, not how many are on this page — the
     * client needs it to say whether there is more to fetch and to report an
     * empty result honestly. */
    total: z.number().int(),
});
/**
 * Creating one. A name, and nothing else required.
 *
 * Money is deliberately absent. It is set from a Budget section on the Event
 * itself — you plan a night's budget while looking at the night — and an Idea
 * usually has neither figure. Decided 2026-08-13 from the prototype.
 */
export const createEventRequestSchema = z.object({
    /* Trimmed before the length is judged, so "   " is empty rather than three
     * characters and two names differing only by padding are one name. */
    name: z.string().trim().min(1).max(EVENT_NAME_MAX),
    date: dateString.nullish(),
    time: timeString.nullish(),
    notes: z.string().max(EVENT_NOTES_MAX).nullish(),
    /* Optional here too — "maybe that place someone mentioned" is where most
     * Events start, and an idea needs no Venue at all. */
    venueId: z.string().uuid().nullish(),
    /* Uuids, like every other id in these routes. A malformed one reaches the
     * Organiser insert and raises `invalid input syntax for type uuid` — an
     * unhandled throw, so a 500 for input the boundary should have refused.
     *
     * Naming somebody who is simply not in the workspace stays a no-op rather
     * than an error: that is a stale client, not a malformed request. */
    organiserIds: z.array(z.string().uuid()).optional(),
});
/**
 * Editing one, field by field.
 *
 * Every key is optional and each saves independently — none is required by
 * another, which is the acceptance criterion this shape exists to satisfy.
 * `null` clears a field; absent leaves it alone, and that distinction is what
 * stops saving the name from wiping a budget somebody set elsewhere.
 */
export const updateEventRequestSchema = z.object({
    name: z.string().trim().min(1).max(EVENT_NAME_MAX).optional(),
    date: dateString.nullish(),
    time: timeString.nullish(),
    notes: z.string().max(EVENT_NOTES_MAX).nullish(),
    plannedBudget: moneySchema.nullish(),
    expectedRevenue: moneySchema.nullish(),
    venueId: z.string().uuid().nullish(),
    /* Uuids, like every other id in these routes. A malformed one reaches the
     * Organiser insert and raises `invalid input syntax for type uuid` — an
     * unhandled throw, so a 500 for input the boundary should have refused.
     *
     * Naming somebody who is simply not in the workspace stays a no-op rather
     * than an error: that is a stale client, not a malformed request. */
    organiserIds: z.array(z.string().uuid()).optional(),
});
/* ── the lifecycle — STORY-012 ─────────────────────────────────────────────── */
export const eventStatusSchema = z.enum(EVENT_STATUSES);
/**
 * The statuses that consume an active-Event entitlement.
 *
 * `pricing.md` meters **active Events**, and the word is doing real work.
 * `idea` is free because the story says so outright — ideas are unmetered and
 * committing to one is the meter. `completed` and `cancelled` release the
 * slot because an Event that is over is not active, and the alternative reading
 * makes the Free tier's limit of **1** mean one Event ever rather than one at a
 * time.
 *
 * Moving an Event back to `idea` therefore releases its slot, and moving it
 * out again re-checks the limit — so the release cannot be used to exceed the
 * plan.
 *
 * Defined once, here, and read by the entitlement counter. Two lists would
 * disagree within a sprint, and the disagreement would be invisible: one of
 * them would simply charge the wrong customers.
 */
export const ACTIVE_EVENT_STATUSES = [
    'planned',
    'confirmed',
];
/**
 * Moving an Event.
 *
 * **`from` is required, and that is the whole concurrency story.** Two people
 * looking at the same Event on two phones is ordinary, and without a stated
 * starting point the second write silently overwrites the first — the story
 * asks instead that one wins and the other is TOLD the status changed
 * underneath them. So the caller states what it believed, the server moves the
 * Event only if that is still true, and a mismatch is a conflict carrying the
 * status the Event actually has.
 *
 * It also costs nothing to supply: every caller is looking at the Event, and
 * therefore already knows.
 */
export const transitionEventRequestSchema = z.object({
    from: eventStatusSchema,
    to: eventStatusSchema,
});
/** One move, for the trail. */
export const eventStatusChangeSchema = z.object({
    from: eventStatusSchema,
    to: eventStatusSchema,
    /** ISO-8601 with offset, UTC in storage — `coding.md`. */
    at: z.string(),
    by: eventOrganiserSchema,
});
export const eventHistoryResponseSchema = z.object({
    changes: z.array(eventStatusChangeSchema),
});
/* ── seeing the list — STORY-040 ───────────────────────────────────────────── */
/**
 * The statuses the list shows when nobody has asked for anything.
 *
 * **Not the same set as `ACTIVE_EVENT_STATUSES`, and the collision of the word
 * "active" is worth stating out loud.** The entitlement meter counts what a
 * workspace is *committed to* — `planned` and `confirmed`. This is what a
 * Member wants *on screen*, which includes their ideas: an idea is the thing
 * they are most likely to come back and work on.
 *
 * The two lists differ by exactly one value, they are both reasonably called
 * "active", and merging them would either start charging for ideas or start
 * hiding them. They stay apart, and neither is derived from the other.
 */
export const LISTED_BY_DEFAULT_STATUSES = [
    'idea',
    'planned',
    'confirmed',
];
export const EVENT_SORTS = ['date', 'name', 'status'];
export const SORT_DIRECTIONS = ['asc', 'desc'];
/** A page. Small enough that a phone on a bad connection is not waiting. */
export const EVENTS_PAGE_SIZE = 25;
export const EVENTS_PAGE_MAX = 100;
/**
 * What the list is being asked for.
 *
 * Every field is optional and the defaults are the story: **active only,
 * soonest first, undated last**. A Member who never touches a control gets the
 * view the story describes, which is why "hide inactive" is not a feature here
 * — it is simply what you already see.
 */
export const listEventsQuerySchema = z.object({
    /** Repeatable. Absent means `LISTED_BY_DEFAULT_STATUSES`, never "all". */
    status: z.array(eventStatusSchema).optional(),
    venueId: z.string().uuid().optional(),
    organiserId: z.string().uuid().optional(),
    /**
     * The earliest date to include, inclusive.
     *
     * A concrete date rather than "last 30 days", and the client computes it.
     * The story evaluates a relative range against the **viewer's** today, and
     * the server has no idea what day it is where the reader is standing — a
     * server-side "30 days ago" would answer differently for two people looking
     * at the same link from two continents, and neither would know why.
     */
    from: dateString.optional(),
    sort: z.enum(EVENT_SORTS).optional(),
    direction: z.enum(SORT_DIRECTIONS).optional(),
    limit: z.number().int().min(1).max(EVENTS_PAGE_MAX).optional(),
    offset: z.number().int().min(0).optional(),
});
//# sourceMappingURL=events.js.map